// AdCopy AI Adoption — Ad Copy section, deterministic check.
// Combines adoption status (mocked lookup against Tinuiti's copytiger_instances table) with
// the account's ad-strength distribution to surface whether AdCopy AI is a fit.

import type { EvidenceProps } from './registry';
import { pct } from '../../utils/format';

interface AdGroupSummary {
  adGroupId: string;
  adGroupName: string;
  campaignName: string;
  rsaCount: number;
  weakCount: number;
  weakPct: number;
}

interface AdCopyAiShape {
  adopted: boolean;
  company: string;
  totalRsas: number;
  distribution: Record<string, number>;
  weakCount: number;
  weakPct: number;
  strongCandidateThreshold: number;
  moderateThreshold: number;
  weakAdGroups: AdGroupSummary[];
}

const STRENGTH_ORDER: { key: string; label: string; tone: 'strong' | 'mid' | 'weak' | 'other' }[] =
  [
    { key: 'EXCELLENT', label: 'Excellent', tone: 'strong' },
    { key: 'GOOD', label: 'Good', tone: 'strong' },
    { key: 'AVERAGE', label: 'Average', tone: 'mid' },
    { key: 'POOR', label: 'Poor', tone: 'weak' },
    { key: 'PENDING', label: 'Pending', tone: 'weak' },
    { key: 'NO_ADS', label: 'Incomplete', tone: 'weak' }
  ];

export const AdCopyAiEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as AdCopyAiShape;

  return (
    <div className='evidence'>
      <p className='evidence__lead'>
        {e.adopted ? (
          <>
            <strong>{e.company}</strong> already has an active AdCopy AI implementation. Across{' '}
            {e.totalRsas} enabled Responsive Search Ads, {pct(e.weakPct)} are weak (Poor / Average /
            Incomplete) — distribution shown below for context.
          </>
        ) : (
          <>
            <strong>{e.company}</strong> does not currently use AdCopy AI. Across {e.totalRsas}{' '}
            enabled Responsive Search Ads, <strong>{pct(e.weakPct)}</strong> ({e.weakCount} of{' '}
            {e.totalRsas}) are weak (Poor / Average / Incomplete). Early experiments from e.l.f. saw{' '}
            <strong>+15% CTR</strong> and <strong>+20% revenue / ROAS lift</strong> after AdCopy AI
            rollout — that's the expected upside framing.
          </>
        )}
      </p>

      <div className='adcopy-strength-band'>
        {STRENGTH_ORDER.map(s => (
          <div
            key={s.key}
            className={`adcopy-strength-band__cell adcopy-strength-band__cell--${s.tone}`}
          >
            <div className='adcopy-strength-band__num'>
              {(e.distribution[s.key] ?? 0).toLocaleString()}
            </div>
            <div className='adcopy-strength-band__lbl'>{s.label}</div>
          </div>
        ))}
      </div>

      {e.weakAdGroups.length > 0 && (
        <section className='evidence__section'>
          <h4 className='evidence__section-title'>
            Ad groups with the highest weak-ad share
            <span className='evidence__section-tag evidence__section-tag--context'>
              top {e.weakAdGroups.length}
            </span>
          </h4>
          <table className='evidence__table evidence__table--adcopy-ai'>
            <thead>
              <tr>
                <th>Ad group</th>
                <th>Campaign</th>
                <th className='evidence__th--right'>RSAs</th>
                <th className='evidence__th--right'>Weak</th>
                <th className='evidence__th--right'>Weak %</th>
              </tr>
            </thead>
            <tbody>
              {e.weakAdGroups.map(g => (
                <tr key={g.adGroupId}>
                  <td>
                    <div>{g.adGroupName}</div>
                    {g.adGroupId !== 'unknown' && (
                      <div className='evidence__action-id'>{g.adGroupId}</div>
                    )}
                  </td>
                  <td>{g.campaignName}</td>
                  <td className='evidence__td--right'>{g.rsaCount.toLocaleString()}</td>
                  <td className='evidence__td--right'>{g.weakCount.toLocaleString()}</td>
                  <td className='evidence__td--right'>{pct(g.weakPct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <p className='evidence__footnote'>
        Eligibility thresholds: ≥ {pct(e.strongCandidateThreshold)} weak ads = strong candidate
        (large); ≥ {pct(e.moderateThreshold)} = moderate (medium); below that = small. Adoption is
        checked via Tinuiti&apos;s <code>copytiger_instances</code> table (mocked in this
        prototype).
      </p>
    </div>
  );
};
